import {
	AfterViewInit,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	EventEmitter,
	Input,
	OnDestroy,
	OnInit,
	Output,
	ViewChild
} from '@angular/core';
import { FormControl } from '@angular/forms';
import {
	AiBorderComponent,
	AiRecommendedTitleComponent,
	ChipComponent,
	PrimaryButtonComponent,
	PrimaryTextareaComponent,
	TypographyComponent
} from 'components';
import { NgForOf, NgIf } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';

import {
	AnaCampaignStart,
	AnaCampaignStartKind
} from '@brand-lolly/app/_models/ana-assistant.model';

const CAMPAIGN_START_MAX_LENGTH = 3000;

/** The chosen kind as the Create New Campaign menu and the product name it. */
const KIND_LABELS: Record<AnaCampaignStartKind, string> = {
	influencer: 'Influencer',
	ads: 'Partner Ads',
	affiliate: 'Affiliate'
};

/**
 * Ready-made first messages under the composer, per kind; the generic set
 * covers a start opened without one.
 */
const SUGGESTIONS_ANY: readonly string[] = [
	'Promote our new vegan protein bar to fitness fans in the US with 15 Instagram creators, $4,000 budget',
	'Grow sign-ups for our budgeting app among Gen Z on TikTok, paying creators 20% commission on every subscription',
	'Boost our winter skincare launch by running paid ads through 5 beauty creators, $8,000 ad spend',
	'Get 30 YouTube tech reviewers to unbox our wireless earbuds in the UK and Germany, $10,000 budget'
];

const SUGGESTIONS_BY_KIND: Record<AnaCampaignStartKind, readonly string[]> = {
	influencer: [
		'Launch our new SPF serum in the US with 20 TikTok beauty creators aged 20-35, $5,000 budget',
		'Find 10 YouTube home-cooking creators in the UK to review our cast-iron pan, $3,000 budget plus free product',
		'Promote our running shoes to marathon runners on Instagram and TikTok in Germany, 25 micro-influencers, $6,000',
		'Get 15 Instagram mom creators in Canada to post about our organic baby snacks, $2,500 budget'
	],
	ads: [
		'Run partner ads for our meal-kit service through 5 US food creators on Instagram and Facebook, $10,000 ad spend',
		'Whitelist 3 fitness creators to run TikTok Spark Ads for our protein powder, $7,500 over 4 weeks',
		'Boost the best-performing reels from our skincare creators as Meta ads to women 25-40 in the US, $5,000',
		'Promote our language-learning app with creator-led ads in Spain and Mexico, $8,000 budget, install goal'
	],
	affiliate: [
		'Recruit 50 Instagram and TikTok fashion creators in the US to sell our sneakers for 15% commission',
		'Launch an affiliate program for our coffee subscription with 20% recurring commission for food bloggers',
		'Invite YouTube tech reviewers to promote our phone accessories, 10% commission plus a $50 bonus per 10 sales',
		'Grow sales of our yoga mats through wellness creators in the UK, 12% commission and a free starter kit'
	]
};

/**
 * "Describe your campaign" — the start screen Ana's panel shows in place of its
 * greeting when Create New Campaign opens it. The field is prefilled with the
 * flow's draft and the caret sits at its end; Continue (or Enter) emits the
 * text, which the facade sends as the brand's first message. A kind picked
 * before opening shows as a chip; it travels beside the text, not in it.
 */
@Component({
	standalone: true,
	selector: 'app-ana-campaign-start',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		NgIf,
		NgForOf,
		AiBorderComponent,
		AiRecommendedTitleComponent,
		ChipComponent,
		TypographyComponent,
		PrimaryTextareaComponent,
		PrimaryButtonComponent
	],
	template: `
		<form class="start" (submit)="onSubmit($event)">
			<div class="start__head">
				<app-chip
					*ngIf="kindLabel"
					class="start__kind"
					variant="blue-white"
				>
					{{ kindLabel }}
				</app-chip>
				<typography variant="h/1" class="start__title" [ariaLevel]="2">
					Describe your campaign
				</typography>
				<typography variant="p/base_medium" class="start__subtitle">
					Tell Ana what you're promoting, who it's for and your
					budget. She'll set up the campaign for you to review.
				</typography>
			</div>

			<div class="start__composer">
				<div
					class="start__surface"
					[class.start__surface--focused]="focused"
					(focusin)="focused = true"
					(focusout)="focused = false"
				>
					<app-primary-textarea
						#field
						class="start__field"
						[inputControl]="control"
						[maxLength]="maxLength"
						placeholder="Launch our new SPF serum in the US with 20 TikTok beauty creators, $5,000 budget, 15% affiliate commission…"
						(keydown)="onKeyDown($event)"
					></app-primary-textarea>
				</div>
			</div>

			<div class="start__foot">
				<typography variant="p/xxs_regular" class="start__disclaimer">
					This feature uses AI. Always check for accuracy.
				</typography>
				<button
					primary
					type="submit"
					borderRadiusSize="squareSmall"
					[disabled]="!canContinue"
				>
					Continue
				</button>
			</div>

			<div class="start__suggestions" *ngIf="suggestions.length">
				<app-ai-recommended-title
					aiRecommendedTitle="AI Suggestions"
				></app-ai-recommended-title>
				<div class="start__suggestion-grid">
					<app-ai-border
						class="start__suggestion"
						*ngFor="let suggestion of suggestions"
					>
						<button
							type="button"
							class="start__suggestion-body"
							(click)="useSuggestion(suggestion)"
						>
							<typography variant="p/sm_regular">
								{{ suggestion }}
							</typography>
						</button>
					</app-ai-border>
				</div>
			</div>
		</form>
	`,
	styles: [
		`
			@use 'ana-gradient';

			:host {
				display: flex;
				flex-direction: column;
				flex: 1 1 auto;
				min-height: 0;
			}

			.start {
				width: 100%;
				max-width: 47.5rem;
				margin: auto;
				padding: 2rem 0 12vh;
				display: flex;
				flex-direction: column;
				gap: 1.5rem;

				&__head {
					display: flex;
					flex-direction: column;
					gap: 0.5rem;
				}

				&__title {
					color: #121212;
					font-size: 1.875rem;
					font-weight: 700;
					line-height: 2.125rem;
					text-wrap: balance;
				}

				&__subtitle {
					color: #8e8e8e;
					font-size: 0.9375rem;
					line-height: 1.375rem;
				}

				&__composer {
					@include ana-gradient.host(1rem, 12px);

					&:has(.start__surface--focused)::before {
						--ana-halo-alpha: 0.18;
					}
				}

				&__surface {
					@include ana-gradient.surface(1rem, 2px, 0, 0);

					&--focused {
						--ana-edge-alpha: 0.3;
					}

					// The library field brings its own grey border and blue focus
					// ring; inside Ana's rim the rim is the border.
					app-primary-textarea.start__field {
						height: 10.75rem;
						border: none;
						outline: none;
						background: transparent;
						font-size: 1rem;
						font-weight: 500;
						color: #111827;

						&:focus-within,
						&:hover {
							border: none;
							outline: none;
						}
					}
				}

				&__foot {
					display: flex;
					align-items: center;
					justify-content: space-between;
					gap: 1rem;
					flex-wrap: wrap;
				}

				&__disclaimer {
					color: rgba(0, 0, 0, 0.35);
				}

				// The AI Search start page's suggestion cards (ai-suggestions),
				// sized down: a prompt is a sentence, not a paragraph.
				&__suggestions {
					display: flex;
					flex-direction: column;
					gap: 0.75rem;
				}

				&__suggestion-grid {
					display: grid;
					grid-template-columns: 1fr 1fr;
					// Every card as tall as the tallest prompt, whatever its length.
					grid-auto-rows: 1fr;
					gap: 0.75rem;
				}

				&__suggestion {
					display: flex;
					// TRAP: ai-border's host is height min-content; without this the card ignores the row height.
					height: 100%;
					border-radius: 1rem;
					cursor: pointer;

					&:hover .start__suggestion-body {
						background-color: #edf4fd;
					}
				}

				&__suggestion-body {
					display: flex;
					align-items: center;
					width: 100%;
					height: 100%;
					min-height: 4.5rem;
					padding: 0.75rem 1.25rem;
					border: none;
					background-color: #fff;
					color: #363636;
					text-align: left;
					cursor: pointer;
					transition: background-color 150ms;

					&:focus-visible {
						outline: 2px solid #0089e0;
						outline-offset: -2px;
					}
				}
			}

			:host(.compact) .start {
				padding: 1.5rem 0 8vh;

				.start__title {
					font-size: 1.5rem;
					line-height: 1.875rem;
				}

				.start__surface app-primary-textarea.start__field {
					height: 8.75rem;
				}

				.start__suggestion-grid {
					grid-template-columns: 1fr;
					gap: 0.5rem;
				}

				.start__suggestion-body {
					min-height: 0;
					padding: 0.625rem 1rem;
				}
			}
		`
	],
	host: {
		'[class.compact]': 'compact'
	}
})
export class AnaCampaignStartComponent
	implements OnInit, AfterViewInit, OnDestroy
{
	/** The flow's entry; a new object resets the field to its draft. */
	@Input() set start(value: AnaCampaignStart | null) {
		if (!value || value === this.current) {
			return;
		}
		this.current = value;
		this.kindLabel = value.kind ? KIND_LABELS[value.kind] : null;
		this.suggestions = value.kind
			? SUGGESTIONS_BY_KIND[value.kind]
			: SUGGESTIONS_ANY;
		this.control.setValue(value.draft);
		if (this.viewReady) {
			this.focusEnd();
		}
	}
	/** The narrow slide-in panel: a smaller title and field. */
	@Input() compact = false;
	/** The trimmed text, on Continue or Enter. */
	@Output() submitted = new EventEmitter<string>();

	@ViewChild('field') private field?: PrimaryTextareaComponent;

	readonly control = new FormControl<string | null>('');
	readonly maxLength = CAMPAIGN_START_MAX_LENGTH;
	canContinue = false;
	focused = false;
	/** The kind the brand already picked, as a chip above the title; null → none. */
	kindLabel: string | null = null;
	/** Ready prompts for the chosen kind, or the generic set without one. */
	suggestions: readonly string[] = SUGGESTIONS_ANY;

	private current: AnaCampaignStart | null = null;
	private viewReady = false;
	private destroy$ = new Subject<void>();

	constructor(private changeDetector: ChangeDetectorRef) {}

	ngOnInit(): void {
		this.canContinue = !!this.control.value?.trim();
		this.control.valueChanges
			.pipe(takeUntil(this.destroy$))
			.subscribe(value => {
				this.canContinue = !!value?.trim();
				this.changeDetector.markForCheck();
			});
	}

	ngAfterViewInit(): void {
		this.viewReady = true;
		this.focusEnd();
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	onKeyDown(event: Event): void {
		const key = event as KeyboardEvent;
		// TRAP: Enter also confirms an IME candidate — submitting on it sends half
		// a word. 229 is what Safari reports while composing.
		if (key.key !== 'Enter' || key.shiftKey) return;
		if (key.isComposing || key.keyCode === 229) return;
		key.preventDefault();
		this.submit();
	}

	/** Fills the field with the prompt for the brand to edit; never sends it. */
	useSuggestion(prompt: string): void {
		this.control.setValue(prompt);
		this.focusEnd();
	}

	onSubmit(event: Event): void {
		event.preventDefault();
		this.submit();
	}

	private submit(): void {
		const text = this.control.value?.trim();
		if (!text) {
			return;
		}
		this.submitted.emit(text);
	}

	/** A tick later: the panel is still opening and the field is not on screen yet. */
	private focusEnd(): void {
		setTimeout(() => {
			// The library types its native textarea as an input element.
			const textarea = this.field?.input?.nativeElement;
			if (!textarea) {
				return;
			}
			const end = textarea.value.length;
			textarea.focus();
			textarea.setSelectionRange(end, end);
			textarea.scrollTop = textarea.scrollHeight;
		});
	}
}
